import { NavLink, Outlet } from "react-router-dom"
import { IconExternalLink, IconLogout } from "@tabler/icons-react"
import { useAdminSecretTap } from "../../context/AdminSecretTapContext"
import { useFeatureFlags } from "../../context/FeatureFlagsProvider"
import { useRequireAuth } from "../../hooks/useRequireAuth"
import { getPublicSiteOrigin, adminPath } from "../../lib/adminHost"

const baseNavItems = [
  { to: adminPath(), label: "Overview", end: true },
  { to: adminPath("events"), label: "Events" },
  { to: adminPath("sermons"), label: "Sermons" },
  { to: adminPath("locations"), label: "Locations" },
  { to: adminPath("giving"), label: "Chapter Giving" },
  { to: adminPath("hero-slides"), label: "Hero Slideshow" },
] as const

const announcementsNavItem = { to: adminPath("announcements"), label: "Announcements" } as const

const trailingNavItems = [
  { to: adminPath("weekly-declaration"), label: "Weekly Declaration" },
  { to: adminPath("message"), label: "Message of the Day" },
  { to: adminPath("prayer-requests"), label: "Submissions" },
  { to: adminPath("upload"), label: "Media Library" },
] as const

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    "block rounded-lg px-3 py-2.5 font-dm-sans text-sm transition",
    isActive
      ? "bg-white/10 font-semibold text-white"
      : "text-[#b8c9e0] hover:bg-white/5 hover:text-white",
  ].join(" ")

const AdminLayout = () => {
  const { user, loading, logout, isDummySession } = useRequireAuth()
  const { flags } = useFeatureFlags()
  const handleSecretTap = useAdminSecretTap()

  const navItems = [
    ...baseNavItems,
    ...(flags.announcements ? [announcementsNavItem] : []),
    ...trailingNavItems,
  ]

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f4f6fb]">
        <span className="loading loading-spinner loading-lg text-[#00205c]" />
      </div>
    )
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#f4f6fb]">
      <aside className="flex h-full w-64 shrink-0 flex-col overflow-hidden bg-[#00205c] text-white">
        <div className="border-b border-white/10 px-5 py-6">
          <div className="flex items-center gap-3">
            <img src="/dominion-city-logo.png" alt="" className="h-9 w-9 object-contain" />
            <div>
              <p className="font-outfit text-sm font-semibold uppercase tracking-wide">Dominion City</p>
              <p className="font-dm-sans text-[10px] uppercase tracking-[0.18em] text-[#9e7e43]">Admin CMS</p>
            </div>
          </div>
        </div>

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {navItems.map(({ to, label, ...rest }) => (
            <NavLink
              key={to}
              to={to}
              end={"end" in rest ? rest.end : undefined}
              className={navLinkClass}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-3 border-t border-white/10 px-4 py-4">
          <a
            href={getPublicSiteOrigin()}
            className="flex items-center gap-2 rounded-lg px-3 py-2 font-dm-sans text-sm text-[#b8c9e0] transition hover:bg-white/5 hover:text-white"
          >
            <IconExternalLink size={16} stroke={1.5} />
            View public site
          </a>
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 font-dm-sans text-sm text-[#fca5a5] transition hover:bg-white/5"
          >
            <IconLogout size={16} stroke={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="shrink-0 border-b border-[#e2e8f0] bg-white px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1
                className="font-outfit text-xl font-semibold tracking-tight text-[#1f1f1f]"
                onClick={handleSecretTap}
              >
                Content Manager
              </h1>
              {user && (
                <p className="mt-0.5 font-dm-sans text-sm text-[#787878]">Signed in as {user.email}</p>
              )}
            </div>
            {isDummySession && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 font-dm-sans text-xs font-medium text-amber-800">
                Dummy session — start backend for full API access
              </span>
            )}
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="mx-auto max-w-5xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
